import { log } from '@domgen/shared/utils/log';
import posthog, { type PostHogConfig } from 'posthog-js';
import { getCountry, getUrlLocale } from '../../utils/translation/country';

/**
 * @description Initializes PostHog analytics with the provided brand and source information. This function checks for the necessary environment variables and sets up PostHog with appropriate configurations, including feature flag handling and user property registration.
 * @param {Object} params - The parameters for initializing PostHog.
 * @param {string} params.brand - The brand associated with the analytics data.
 * @param {string} params.source - The source of the analytics data.
 * @returns {Promise<void>} A promise that resolves when PostHog is successfully initialized, or rejects if there is an error during initialization.
 * @throws {Error} Throws an error if the PostHog API key or host is not defined in the environment variables.
 */
export const initPostHog = ({
  brand,
  source,
}: {
  brand: string;
  source?: string;
}): Promise<void> => {
  const { VITE_POSTHOG_API_KEY, VITE_POSTHOG_HOST } = import.meta.env;

  log.debug({ message: 'Initializing PostHog', data: { brand, source } });

  return new Promise<void>((resolve, reject) => {
    if (!VITE_POSTHOG_API_KEY || !VITE_POSTHOG_HOST) {
      return reject(new Error('PostHog API key or host is not defined in environment variables'));
    }

    // Country comes from the `?lang=` URL param set by the redirect that sends
    // visitors here. A visitor arriving without a recognised `?lang=` gets no
    // locale/country at all rather than being mis-attributed to the app's
    // default locale.
    const locale = getUrlLocale();
    const baseProperties = {
      brand,
      source,
      ...(locale ? { locale, country: getCountry(locale) } : {}),
    };

    posthog.init(VITE_POSTHOG_API_KEY, {
      api_host: VITE_POSTHOG_HOST,
      defaults: '2026-01-30',
      person_profiles: 'identified_only',
      capture_pageview: false, // pageviews are captured manually by the PostHogPageView component
      // Capture $pageleave when the visitor leaves the page. It fires AFTER the LP
      // resolves, so it carries the resolved_lp super-property even for visitors
      // who never click — fixing the "no resolved_lp" gap for bounced sessions
      // (the manual $pageview fires before the LP resolves). Also gives bounce
      // rate and scroll depth. Cookieless-compatible; captures no PII.
      capture_pageleave: true,
      // Nothing is written to or read from the visitor's device. PostHog derives
      // the visitor id server-side from a daily-salted hash, so the ePrivacy /
      // PECR storage-consent rule does not apply and no consent banner is needed.
      // The matching "cookieless server hash mode" setting MUST be enabled on the
      // PostHog project or these events are silently dropped (done for the
      // dedicated EU projects: non-prod 230315, prod 230316).
      cookieless_mode: 'always',
      persistence: 'memory',
      // The built-in survey renderer refuses to load under
      // `cookieless_mode: 'always'` (it persists seen/dismissed state on the
      // device — the very storage the cookieless design forbids), so surveys
      // are rendered by this app instead: see services/posthog/exitSurvey.ts.
      // Disabling them here is explicit and prevents a double popover if a
      // future posthog-js relaxes that gate.
      disable_surveys: true,
      // If PostHog's renderer is ever re-enabled, make it follow the page's
      // `?lang=` rather than the browser language. NOTE its fallback takes the
      // FIRST segment as the language (BCP 47), which misreads this app's
      // country-first locales (be-FR would look for 'be', not 'fr') — one more
      // reason the custom renderer localizes itself. Exists at runtime but is
      // not yet declared in PostHogConfig, hence the cast.
      ...((locale ? { override_display_language: locale } : {}) as Partial<PostHogConfig>),
      // Autocapture records clicks/interactions on the page in addition to the
      // two events we send manually ($pageview and cta_click). It is safe here:
      // this page has no form inputs, so no typed/user-entered data is captured
      // (autocapture never records input VALUES regardless), and it works with
      // cookieless_mode/persistence:'memory'. It surfaces engagement on non-CTA
      // elements (country switcher, footer links, benefits) that the two manual
      // events miss. Keep capture_pageview:false — pageviews stay manual so the
      // country/locale super-properties are attached via before_send.
      autocapture: true,
      // Attaches the base properties to EVERY event, including the very first.
      // `register()` cannot be used for this: super-properties are held in
      // persistence, which does not exist before `init` runs, and with
      // `persistence: 'memory'` there is nothing durable to write them to
      // either. `before_send` runs on each event as it is sent, so nothing can
      // slip out ahead of it — which matters because the first `$pageview` used
      // to go out without a country, and cookieless mode strips the IP so there
      // are no `$geoip_*` properties to fall back on.
      before_send: (event) => {
        if (!event) return event;
        event.properties = { ...baseProperties, ...event.properties };
        return event;
      },
      loaded: (ph) => {
        // On feature flags gets invoked even if the flags request is successful or unsuccessful, so we can use it to ensure that PostHog has finished its initialization process
        // Complete in 3 seconds if the flags request fails to prevent hanging the app indefinitely
        ph.onFeatureFlags(() => {
          resolve();
        });
      },
    });
  });
};
